import React from 'react';
import Tooltip from 'core/components/Tooltip';
import AvatarGroup from '@mui/material/AvatarGroup';
import Image from 'pages/Dashboard/components/Image';
import {
  isNilOrZero,
} from '@populateui/utils';
import values from 'lodash/values';
import head from 'lodash/head';

type Info = {
  title: string;
  color: string;
};

const infoByKeys: Record<string, Info> = {
  appointments: {
    title: 'Appointments',
    color: '!bg-teal-800 !text-white',
  },
  claims: {
    title: 'Claims',
    color: '!bg-indigo-800 !text-white',
  },
  billingProviderRules: {
    title: 'Billing Provider Rules',
    color: '!bg-orange-800 !text-white',
  },
  billingProviderPtans: {
    title: 'PTANs',
    color: '!bg-purple-800 !text-white',
  },
};

type UsageSectionProps = Info & {
  count: number;
};

const UsageTooltip = ({ title, count }: UsageSectionProps) => (
  count > 0 ? (
    <div className="flex gap-2 justify-between font-semibold text-gray-500">
      <span>
        {title}
        :
      </span>
      <span className="text-gray-700">{count}</span>
    </div>
  ) : null
);

function formatCount(count: number) {
  const result = [];
  if (count >= 1000) {
    result.push(`${Math.floor(count / 1000)}k+`);
  } else if (count >= 100) {
    result.push('99+');
  } else {
    result.push(count.toString());
  }

  return head(result);
}

const UsageCount = ({ count, title, color }: UsageSectionProps) => (count > 0 ? (
  <Image
    size="2xs"
    className="font-semibold"
    alt={title}
    classes={{ root: color }}
  >
    {formatCount(count)}
  </Image>
) : null);

type Props = Record<string, number>;

export default function UsesCell({ id, ...props }: Props) {
  const usesKeys = Object.keys(props);
  const noUsage = values(props).every(isNilOrZero);

  return noUsage ? (
    <span className="text-gray-700 font-semibold">—</span>
  ) : (
    <Tooltip
      title={(
        <div className="flex flex-col gap-2 p-2">
          <div>
            <h2 className="font-semibold">Uses</h2>
          </div>
          {usesKeys.map((key) => (
            <UsageTooltip
              key={`tooltip-${id}-${key}`}
              count={props[key] ?? 0}
              {...infoByKeys[key]}
            />
          ))}
        </div>
      )}
      placement="left"
    >
      <span>
        <AvatarGroup
          max={4}
          classes={{ avatar: '!h-5 !w-5 !text-xs' }}
        >
          {usesKeys.map((key) => (
            <UsageCount
              key={`count-${id}-${key}`}
              count={props[key] ?? 0}
              {...infoByKeys[key]}
            />
          ))}
        </AvatarGroup>
      </span>
    </Tooltip>
  );
}
